import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import { AWS_URL, http, URL } from "../../services/http.service";
import { reviewService } from "../../services/review.service";
import { createCustomer } from "../../services/customer.service";
import {
  addToUserCart,
  buyNowUser,
  getUserCart
} from "../../services/cart.service";
import {
  toggleUserWishlistItem,
  toggleGuestWishlistItem,
  checkUserWishlistItem,
  checkGuestWishlistItem
} from "../../services/wishlist.service";
import { AccountLoginDialog } from "./accountLogin";
import { createShortLink, resolveShortLink, getProductShareUrl } from '../../services/shortUrl.service.js';
import {
  FaStar,
  FaRegStar,
  FaShoppingCart,
  FaWhatsapp,
  FaFacebookF,
  FaTwitter,
  FaLink,
  FaShareAlt,
  FaHeart,
  FaRegHeart,
  FaTruck,
  FaShieldAlt,
  FaExchangeAlt,
  FaClock,
  FaCopy,
  FaQrcode,
  FaArrowLeft,
  FaPlus,
  FaMinus,
  FaDownload,
  FaImage,
  FaShareSquare,
} from "react-icons/fa";
import {
  MdClose,
  MdLocalShipping,
  MdShoppingBag,
  MdZoomIn,
  MdEmail,
  MdVerified,
  MdDiscount,
} from "react-icons/md";
import { ReviewSection, VariantSelector } from './storeSetting/productCardfunction';
import { trackAddToCart } from "../../components/common/utlity/tracking.js";
import { getBusinessBySlug } from "../../services/shortBusiness.service.js";
import { getFingerprint } from "../../helpers/fingerprint";

// Dummy image component for fallback
const DummyImage = ({ className, onClick, icon: Icon = FaImage }) => (
  <div 
    className={`bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${className}`}
    onClick={onClick}
  >
    <Icon className="w-16 h-16 text-gray-400" />
  </div>
);

// QR Code Modal with download and share options
const QRCodeModal = ({ url, onClose, theme, productName }) => {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const generateQR = async () => {
      try {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
        setQrCode(qrUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      } finally {
        setLoading(false);
      }
    };
    generateQR();
  }, [url]);

  const downloadQRCode = async () => {
    try {
      setDownloading(true);
      const response = await fetch(qrCode);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${productName || 'product'}-qrcode.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.QR_DOWNLOADED"));
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.QR_DOWNLOAD_FAILED"));
    } finally {
      setDownloading(false);
    }
  };

 const shareViaWhatsApp = async () => {
  try {
    setSharing(true);
    
    // Download the QR code first
    await downloadQRCode();
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile: Show instructions and open WhatsApp
      toast.info(t("STOREPRODUCT.QR_MODAL.WHATSAPP_MOBILE_MESSAGE"), { autoClose: 5000 });
      
      setTimeout(() => {
        const message = encodeURIComponent(t("STOREPRODUCT.QR_MODAL.WHATSAPP_MESSAGE", { productName }));
        window.location.href = `whatsapp://send?text=${message}`;
      }, 1500);
    } else {
      toast.info(t("STOREPRODUCT.QR_MODAL.WHATSAPP_DESKTOP_MESSAGE"), { autoClose: 6000 });
      
      setTimeout(() => {
        const message = encodeURIComponent(t("STOREPRODUCT.QR_MODAL.WHATSAPP_MESSAGE", { productName }));
        window.open(`https://web.whatsapp.com/send?text=${message}`, '_blank');
      }, 1500);
    }
  } catch (error) {
    console.error('Error:', error);
    toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.QR_SHARE_FAILED"));
  } finally {
    setSharing(false);
  }
};

  const shareViaEmail = () => {
    const subject = encodeURIComponent(t("STOREPRODUCT.QR_MODAL.EMAIL_SUBJECT", { productName }));
    const body = encodeURIComponent(
      `Here's the QR code for ${productName}.\n\n` +
      `Scan it to view the product or visit: ${url}\n\n` +
      `QR Code URL: ${qrCode}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleNativeShare = async () => {
    try {
      setSharing(true);
      const response = await fetch(qrCode);
      const blob = await response.blob();
      const file = new File([blob], `${productName || 'product'}-qrcode.png`, { type: 'image/png' });
      
      if (navigator.share) {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: t("STOREPRODUCT.QR_MODAL.SHARE_TITLE", { productName }),
            text: t("STOREPRODUCT.QR_MODAL.SHARE_TEXT", { productName }),
            files: [file]
          });
        } else {
          await navigator.share({
            title: t("STOREPRODUCT.QR_MODAL.SHARE_TITLE", { productName }),
            text: t("STOREPRODUCT.QR_MODAL.SHARE_TEXT", { productName }),
            url: url
          });
        }
        toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.SHARED_SUCCESS"));
      } else {
        toast.info(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.SHARE_NOT_SUPPORTED"));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.QR_SHARE_FAILED"));
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-fadeInUp"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <MdClose className="h-5 w-5 text-gray-600" />
        </button>

        <h3 className="text-xl font-bold mb-4 text-center" style={{ color: theme?.headingColor }}>
          {t("STOREPRODUCT.QR_MODAL.TITLE")}
        </h3>

        <div className="flex flex-col items-center justify-center p-4">
          {loading ? (
            <div className="w-48 h-48 bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
              <span className="text-gray-400">{t("STOREPRODUCT.QR_MODAL.GENERATING")}</span>
            </div>
          ) : (
            <>
              <img 
                src={qrCode} 
                alt={t("STOREPRODUCT.QR_MODAL.TITLE")} 
                className="w-48 h-48 rounded-lg shadow-lg"
              />
              
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={downloadQRCode}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  style={{ backgroundColor: theme?.primaryColor }}
                >
                  <FaDownload className="h-4 w-4" />
                  {downloading ? t("STOREPRODUCT.QR_MODAL.DOWNLOADING") : t("STOREPRODUCT.QR_MODAL.DOWNLOAD")}
                </button>
                
                <button
                  onClick={shareViaEmail}
                  disabled={sharing}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                  title={t("STOREPRODUCT.QR_MODAL.SHARE_VIA_EMAIL")}
                >
                  <MdEmail className="h-5 w-5" />
                </button>

                {navigator.share && (
                  <button
                    onClick={handleNativeShare}
                    disabled={sharing}
                    className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                    title={t("STOREPRODUCT.QR_MODAL.SHARE")}
                  >
                    <FaShareSquare className="h-5 w-5" />
                  </button>
                )}
              </div>
            </>
          )}
          
          <p className="mt-4 text-sm text-gray-600 text-center">
            {t("STOREPRODUCT.QR_MODAL.SCAN_MESSAGE")}
          </p>
          
          <div className="mt-4 w-full p-3 bg-gray-50 rounded-lg flex items-center justify-between">
            <span className="text-sm text-gray-600 truncate flex-1 mr-2">
              {url}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(url);
                toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LINK_COPIED"));
              }}
              className="p-2 rounded-lg hover:bg-white transition-colors"
              style={{ color: theme?.primaryColor }}
            >
              <FaCopy className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-2 text-xs text-gray-400 text-center">
            {t("STOREPRODUCT.QR_MODAL.SHARE_QR_MESSAGE")}
          </p>
        </div>
      </div>
    </div>
  );
};

function ProductPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { businessName, bussinessUuid, productId, shortCode } = useParams();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [newReviewAdded, setNewReviewAdded] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [checkingWishlist, setCheckingWishlist] = useState(true);
  const [togglingWishlist, setTogglingWishlist] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [shortLink, setShortLink] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isShortUrl, setIsShortUrl] = useState(false);
  const [resolvedProductId, setResolvedProductId] = useState(productId);
  const [resolvedBusinessUuid, setResolvedBusinessUuid] = useState(bussinessUuid);
  const [isCreatingShortLink, setIsCreatingShortLink] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // NEW: State to track if current variant is in cart
  const [isVariantInCart, setIsVariantInCart] = useState(false);
  
  const isB2BMode = localStorage.getItem('b2b_Price') === 'true' || localStorage.getItem('b2b_intent') === 'true';
  const business = useSelector((state) => state?.bussiness?.business) || null;
  const customer = useSelector((state) => state?.customer) || null;
  const customerProfile = customer?.profile || null;
  const token = useSelector((state) => state?.auth?.token) || localStorage.getItem('token');

  // ============== CART STATE MANAGEMENT ==============
  const [cart, setCart] = useState([]);

  // Get current business UUID (resolved from short link or from URL)
  const getCurrentBusinessUuid = () => {
    return resolvedBusinessUuid || bussinessUuid;
  };

  // Get user profile from localStorage
  const getUserProfile = () => {
    const currentBusinessUuid = getCurrentBusinessUuid();
    if (!currentBusinessUuid) return null;
    
    const savedUser = localStorage.getItem(`user_${currentBusinessUuid}`);
    const savedGuest = localStorage.getItem(`guest_${currentBusinessUuid}`);
    return savedUser
      ? JSON.parse(savedUser)
      : savedGuest
        ? JSON.parse(savedGuest)
        : null;
  };

  // Check if user is logged in (not guest)
  const isUserLoggedIn = () => {
    const userProfile = getUserProfile();
    return userProfile && !userProfile.isGuest;
  };

  // Save cart to localStorage for backward compatibility
  useEffect(() => {
    const currentBusinessUuid = getCurrentBusinessUuid();
    if (currentBusinessUuid && cart.length > 0) {
      localStorage.setItem(`cart_${currentBusinessUuid}`, JSON.stringify(cart));
    }
  }, [cart, getCurrentBusinessUuid()]);

  // URL params logging
useEffect(() => {
    
    if (!bussinessUuid && businessName) {
      const fetchBusinessUuid = async () => {
        try {
          const response = await getBusinessBySlug(businessName);
          if (response.data?.success && response.data?.data?.uuid) {
            setResolvedBusinessUuid(response.data.data.uuid);
          }
        } catch (error) {
          console.error('Error fetching business UUID:', error);
        }
      };
      fetchBusinessUuid();
    }
  }, [businessName, bussinessUuid, productId, shortCode]);

  // State for product image popup
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });

  // Theme styles
  const theme = {
    primaryColor: business?.primaryColor || '#4f46e5',
    secondaryColor: business?.secondaryColor || '#10b981',
    buttonColor: business?.buttonColor || '#4f46e5',
    textColor: business?.textColor || '#1f2937',
    headingColor: business?.headingColor || '#111827',
    fontFamily: business?.fontFamily || 'Inter, sans-serif',
    borderRadius: business?.borderRadius || '8px',
    buttonStyle: business?.buttonStyle || 'rounded'
  };

  const themeStyles = {
    primaryColor: theme?.primaryColor || '#4f46e5',
    secondaryColor: theme?.secondaryColor || '#10b981',
    buttonColor: theme?.buttonColor || '#4f46e5',
    textColor: theme?.textColor || '#1f2937',
    headingColor: theme?.headingColor || '#111827',
    backgroundColor: '#ffffff',
    fontFamily: theme?.fontFamily || 'Inter, sans-serif',
    borderRadius: theme?.borderRadius || '8px',
    buttonStyle: theme?.buttonStyle || 'rounded'
  };

  const getButtonBorderRadius = () => {
    switch (themeStyles.buttonStyle) {
      case 'pill': return '9999px';
      case 'square': return '4px';
      case 'rounded':
      default: return themeStyles.borderRadius;
    }
  };

  // Check if current URL is short URL format
  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length === 4 && pathParts[2] === 'p') {
      setIsShortUrl(true);
    }
  }, []);

  // Auto-resolve short URL if needed
  useEffect(() => {
    const resolveShortUrl = async () => {
      if (isShortUrl && shortCode && businessName) {
        try {
          setLoading(true);
          
          try {
            const data = await resolveShortLink(shortCode, businessName);
            
            if (data) {
              const { businessUuid, productId } = data;
              setResolvedBusinessUuid(businessUuid);
              setResolvedProductId(productId);
              
              window.history.replaceState(
                {}, 
                '', 
                `/${businessName}/store/${businessUuid}/product/${productId}`
              );
              
              setIsShortUrl(false);
              return;
            }
          } catch (apiError) {
            console.log('API resolve failed:', apiError);
          }
          
          setError(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LINK_EXPIRED"));
          setLoading(false);
          
        } catch (err) {
          console.error('Error resolving short URL:', err);
          setError(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LOAD_FAILED"));
          setLoading(false);
        }
      }
    };

    resolveShortUrl();
  }, [isShortUrl, shortCode, businessName, t]);

  // Load cart for logged-in users
  useEffect(() => {
    const loadUserCart = async () => {
      const currentBusinessUuid = getCurrentBusinessUuid();
      if (!currentBusinessUuid) return;
      
      const savedUser = localStorage.getItem(`user_${currentBusinessUuid}`);
      if (savedUser) {
        try {
          const userProfile = JSON.parse(savedUser);
          const customerId = userProfile.customerId || userProfile._id;
          
          const response = await getUserCart(currentBusinessUuid, customerId);
          
          if (response && response.success && response.data && response.data.items) {
            setCart(response.data.items);
          }
        } catch (error) {
          console.error("Error loading user cart:", error);
        }
      } else {
        const savedCart = localStorage.getItem(`cart_${currentBusinessUuid}`);
        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }
      }
    };

    loadUserCart();
  }, [getCurrentBusinessUuid()]);

  // Generate short link for this product
  const generateShortLinkForProduct = async () => {
    if (!product || !productId || !bussinessUuid) return getProductShareUrl(businessName, bussinessUuid, productId);
    
    try {
      setIsCreatingShortLink(true);
      const formattedSlug = (businessName || business?.businessName || "store").toLowerCase().replace(/\s+/g, '-');
      
      const shortUrl = await createShortLink({
        businessUuid: bussinessUuid,
        businessSlug: formattedSlug,
        productId: productId,
        token: token
      });
      
      setShortLink(shortUrl);
      return shortUrl;
      
    } catch (error) {
      console.error('Error creating short link:', error);
      return getProductShareUrl(businessName, bussinessUuid, productId);
    } finally {
      setIsCreatingShortLink(false);
    }
  };

  const getShareMessage = () => {
    if (!product) return '';
    const price = getDisplayPrice();
    const productName = getDisplayName();
    return t("STOREPRODUCT.PRODUCT_PAGE.SHARE_MESSAGE", { 
      productName, 
      price, 
      storeName: businessName || "Store" 
    });
  };

  // Share functions
  const shareOnWhatsApp = async () => {
    const url = shortLink || await generateShortLinkForProduct();
    const message = encodeURIComponent(getShareMessage() + ' ' + url);
    window.open(`https://wa.me/?text=${message}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnFacebook = async () => {
    const url = encodeURIComponent(shortLink || await generateShortLinkForProduct());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnTwitter = async () => {
    const url = shortLink || await generateShortLinkForProduct();
    const text = encodeURIComponent(getShareMessage() + ' ' + url);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    setShowShareMenu(false);
  };

  const shareViaEmail = async () => {
    const subject = encodeURIComponent(t("STOREPRODUCT.PRODUCT_PAGE.EMAIL_SUBJECT", { productName: getDisplayName() }));
    const body = encodeURIComponent(`${getShareMessage()}\n\n${shortLink || await generateShortLinkForProduct()}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowShareMenu(false);
  };

  const copyToClipboard = async () => {
    try {
      const url = shortLink || await generateShortLinkForProduct();
      await navigator.clipboard.writeText(url);
      toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LINK_COPIED"));
    } catch (err) {
      toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.COPY_FAILED"));
    }
    setShowShareMenu(false);
  };

  // Fetch product data
  useEffect(() => {
    if (resolvedBusinessUuid && resolvedProductId && !isShortUrl) {
      fetchProduct();
    }
  }, [resolvedBusinessUuid, resolvedProductId, isShortUrl]);

  // Generate short link when product loads
  useEffect(() => {
    if (product && productId && bussinessUuid && !shortLink) {
      generateShortLinkForProduct();
    }
  }, [product, productId, bussinessUuid]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await http.get(`${URL}/inventory/${resolvedBusinessUuid}/product/${resolvedProductId}`);
      
      if (response.data?.success && response.data?.data) {
        setProduct(response.data.data);
      } else if (response.data) {
        setProduct(response.data);
      } else {
        setError(t("STOREPRODUCT.PRODUCT_PAGE.PRODUCT_NOT_FOUND"));
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err?.response?.data?.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LOAD_FAILED"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      if (product.hasVariants && product.variants?.length > 0) {
        const availableVariant = product.variants.find((v) => v.quantity > 0) || product.variants[0];
        setSelectedVariant(availableVariant);
      } else {
        setSelectedVariant({
          ...product,
          variantName: product.productName,
          quantity: product.quantity || 0,
          salePrice: product.salePrice || "0",
          isBaseProduct: true,
        });
      }
    }
  }, [product]);

  // Check if current variant is in cart
  useEffect(() => {
    if (!selectedVariant || !product || cart.length === 0) {
      setIsVariantInCart(false);
      return;
    }

    const checkIfVariantInCart = () => {
      if (product.hasVariants && selectedVariant.variantId) {
        return cart.some(item => 
          item.productId === product.productId && 
          item.variantId === selectedVariant.variantId
        );
      }
      
      return cart.some(item => item.productId === product.productId);
    };

    setIsVariantInCart(checkIfVariantInCart());
  }, [selectedVariant, cart, product]);

  // Check wishlist status when variant changes
  useEffect(() => {
    if (product?.productId && resolvedBusinessUuid && selectedVariant) {
      checkWishlistStatus();
    }
  }, [selectedVariant, product?.productId, resolvedBusinessUuid]);

  // Check if item is in wishlist
  const checkWishlistStatus = async () => {
    if (!resolvedBusinessUuid || !product?.productId) return;
    
    setCheckingWishlist(true);
    try {
      const userProfile = getUserProfile();
      let response;
      
      if (userProfile && !userProfile.isGuest) {
        const customerId = userProfile.customerId || userProfile._id;
        response = await checkUserWishlistItem(
          resolvedBusinessUuid,
          customerId,
          product.productId,
          selectedVariant?.variantId || null
        );
      } else {
        response = await checkGuestWishlistItem(
          resolvedBusinessUuid,
          product.productId,
          selectedVariant?.variantId || null
        );
      }
      
      if (response && response.success) {
        setIsWishlisted(response.exists);
      }
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    } finally {
      setCheckingWishlist(false);
    }
  };

  // Toggle wishlist
  const handleToggleWishlist = async () => {
    if (!resolvedBusinessUuid || !product?.productId) return;
    
    setTogglingWishlist(true);
    try {
      const wishlistItem = {
        productId: product.productId,
        variantId: selectedVariant?.variantId || null,
        variantKey: selectedVariant?.variantKey || null,
        variantAttributes: selectedVariant?.variantAttributes || [],
        productName: getDisplayName(),
        salePrice: getDisplayPrice(),
        b2bSalePrice: product.b2bSalePrice || 0,
        image: getDisplayImage(),
        sku_number: product.sku_number || '',
        weight: product.weight || 0.5,
        taxable_v: product.taxable_v || 'non-gst',
        cgst: product.cgst || 0,
        sgst_utgst: product.sgst_utgst || 0,
        igst: product.igst || 0,
        cess: product.cess || 0,
        isB2B: isB2BMode,
        variantName: selectedVariant?.variantName || null,
        variantSku: selectedVariant?.sku || selectedVariant?.variantSku || null,
        variantStock: selectedVariant?.quantity || 0,
        variantSalePrice: selectedVariant?.salePrice || 0,
        variantPurchasePrice: selectedVariant?.purchasePrice || 0,
        variantMrp: selectedVariant?.mrp || 0,
        variantBatchNumber: selectedVariant?.batchNumber || null,
        hasVariants: product.hasVariants || false,
        quantity: selectedVariant?.quantity || product.quantity || 0,
        mrp: selectedVariant?.mrp || product.mrp || 0
      };
      
      const userProfile = getUserProfile();
      let response;
      
      if (userProfile && !userProfile.isGuest) {
        const customerId = userProfile.customerId || userProfile._id;
        response = await toggleUserWishlistItem(resolvedBusinessUuid, customerId, wishlistItem);
      } else {
        response = await toggleGuestWishlistItem(resolvedBusinessUuid, wishlistItem);
      }
      
      if (response && response.success) {
        setIsWishlisted(!isWishlisted);
        toast.success(
          !isWishlisted 
            ? t('PRODUCT_PAGE.ADDED_TO_WISHLIST') 
            : t('PRODUCT_PAGE.REMOVED_FROM_WISHLIST')
        );
      } else {
        toast.error(response?.message || t('PRODUCT_PAGE.WISHLIST_ERROR'));
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error(t('PRODUCT_PAGE.WISHLIST_ERROR'));
    } finally {
      setTogglingWishlist(false);
    }
  };

  useEffect(() => {
    if (product && resolvedBusinessUuid) {
      loadReviewStats();
    }
  }, [product, resolvedBusinessUuid, newReviewAdded]);

  const loadReviewStats = async () => {
    try {
      setLoadingStats(true);
      const statsResp = await reviewService.getProductReviewStats(
        resolvedBusinessUuid,
        resolvedProductId,
      );

      let normalizedStats = {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      };

      if (statsResp) {
        normalizedStats.averageRating = statsResp.averageRating || 0;
        normalizedStats.totalReviews = statsResp.totalReviews || 0;
        normalizedStats.ratingDistribution = statsResp.ratingDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      }

      setReviewStats(normalizedStats);
    } catch (error) {
      console.error("Error loading review stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Fixed getAllImages with safe filtering
  const getAllImages = () => {
    const images = [];
    
    if (product?.itemPicture) {
      images.push(product.itemPicture);
    }
    
    if (product?.hasVariants && product?.variants) {
      product.variants.forEach(variant => {
        if (variant.itemPicture && !images.includes(variant.itemPicture)) {
          images.push(variant.itemPicture);
        }
      });
    }
    
    return images;
  };

  // Fixed openProductImagePopup with safe image handling
  const openProductImagePopup = (index = 0) => {
    const productImages = getAllImages();
    
    if (productImages.length === 0) {
      setImagePopup({
        isOpen: true,
        images: [{
          url: null,
          alt: getDisplayName(),
          isDummy: true
        }],
        currentIndex: 0,
      });
      return;
    }

    const formattedImages = productImages.map(img => ({
      url: img ? `${AWS_URL}${img}` : null,
      alt: getDisplayName(),
      isDummy: !img
    }));

    setImagePopup({
      isOpen: true,
      images: formattedImages,
      currentIndex: Math.min(index, formattedImages.length - 1),
    });
  };

  const closeImagePopup = () => {
    setImagePopup({
      isOpen: false,
      images: [],
      currentIndex: 0,
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!imagePopup.isOpen) return;
      if (e.key === "Escape") closeImagePopup();
      if (e.key === "ArrowLeft") {
        setImagePopup(prev => ({
          ...prev,
          currentIndex: prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.images.length - 1
        }));
      }
      if (e.key === "ArrowRight") {
        setImagePopup(prev => ({
          ...prev,
          currentIndex: prev.currentIndex < prev.images.length - 1 ? prev.currentIndex + 1 : 0
        }));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [imagePopup.isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showShareMenu && !e.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showShareMenu]);

  const getLowestVariantPrice = () => {
    if (!product) return "0";
    if (!product.hasVariants || !product.variants?.length) {
      return product.salePrice || "0";
    }

    let lowestPrice = Infinity;
    product.variants.forEach((variant) => {
      const variantPrice = parseFloat(variant.salePrice || "0");
      if (variantPrice > 0 && variantPrice < lowestPrice) {
        lowestPrice = variantPrice;
      }
    });

    return lowestPrice === Infinity ? product.salePrice || "0" : lowestPrice.toString();
  };

  const getDisplayPrice = () => {
    if (!product) return "0";
    if (selectedVariant?.salePrice) return selectedVariant.salePrice;
    return getLowestVariantPrice();
  };

  const getOriginalPrice = () => {
    if (!product) return "0";
    if (selectedVariant?.mrp) return selectedVariant.mrp;
    return product.originalPrice || product.mrp || "0";
  };

  const getDiscountPercentage = () => {
    const currentPrice = parseFloat(getDisplayPrice());
    const original = parseFloat(getOriginalPrice());
    if (original > 0 && currentPrice < original) {
      return Math.round(((original - currentPrice) / original) * 100);
    }
    return product?.discount ? parseInt(product.discount) : 0;
  };

  const getDisplayQuantity = () => {
    if (!product) return 0;
    if (selectedVariant?.quantity !== undefined) return selectedVariant.quantity;
    return product.quantity || 0;
  };

  const getDisplayImage = () => {
    if (!product) return null;
    
    if (selectedVariant?.itemPicture) {
      return selectedVariant.itemPicture;
    }
    
    if (product.itemPicture) {
      return product.itemPicture;
    }
    
    return null;
  };

  const getDisplayName = () => {
    if (!product) return "";
    if (selectedVariant?.variantName && selectedVariant.variantName !== product.productName) {
      return `${product.productName} - ${selectedVariant.variantName}`;
    }
    return product.productName;
  };

  // ============== CART FUNCTIONS ==============
  const createCartItem = () => {
    if (!product) throw new Error("Product not found");
    if (product.hasVariants && !selectedVariant) throw new Error(t("STOREPRODUCT.PRODUCT_PAGE.SELECT_VARIANT"));
    if (getDisplayQuantity() <= 0) throw new Error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.VARIANT_OUT_OF_STOCK"));

    let variantAttributes = [];
    
    if (selectedVariant?.attributes && selectedVariant.attributes.length > 0) {
      variantAttributes = selectedVariant.attributes;
    } else if (selectedVariant?.variantAttributes) {
      variantAttributes = selectedVariant.variantAttributes;
    } else if (selectedVariant?.variantName) {
      variantAttributes = [{
        type: 'variant',
        value: selectedVariant.variantName
      }];
    }

    const formattedAttributes = variantAttributes.map(attr => ({
      type: String(attr.type || attr.attributeType || '').toLowerCase(),
      value: String(attr.value || '')
    })).filter(attr => attr.type && attr.value);

    const price = isB2BMode
      ? (selectedVariant?.b2bSalePrice ?? selectedVariant?.salePrice ?? product.b2bSalePrice ?? product.salePrice)
      : (selectedVariant?.salePrice ?? product.salePrice);

    return {
      _id: product._id || product.productId,
      productId: product.productId || product._id,
      productName: product.productName,
      productCategory: product.productCategory || '',
      sku_number: product.sku_number || '',
      image: getDisplayImage(),
      itemPicture: getDisplayImage(),
      
      salePrice: Number(price || 0),
      b2bSalePrice: Number(selectedVariant?.b2bSalePrice || product.b2bSalePrice || 0),
      price: Number(price || 0),
      cartQuantity: quantity,
      quantity: getDisplayQuantity(),
      
      weight: product.weight || 0.5,
      taxable_v: product.taxable_v || 'taxable',
      cgst: product.cgst || 0,
      sgst_utgst: product.sgst_utgst || 0,
      igst: product.igst || 0,
      cess: product.cess || 0,
      
      hasVariants: product.hasVariants || formattedAttributes.length > 0 || !!selectedVariant,
      variantId: selectedVariant?.variantId || null,
      variantName: selectedVariant?.variantName || 
                  (formattedAttributes.length > 0 ? formattedAttributes.map(a => a.value).join(' - ') : null),
      variantAttributes: formattedAttributes,
      variantSku: selectedVariant?.sku || selectedVariant?.variantSku || null,
      variantStock: selectedVariant?.quantity || 0,
      variantSalePrice: Number(selectedVariant?.salePrice || price || 0),
      variantPurchasePrice: Number(selectedVariant?.purchasePrice || 0),
      variantMrp: Number(selectedVariant?.mrp || product.mrp || 0),
      variantBatchNumber: selectedVariant?.batchNumber || null,
      
      selectedVariant: selectedVariant,
      
      displayPrice: getDisplayPrice(),
      displayQuantity: getDisplayQuantity(),
      displayName: getDisplayName(),
      displayImage: getDisplayImage(),
      
      isB2B: isB2BMode,
      b2bMinimumOrderQuantity: product.b2bMinimumOrderQuantity || selectedVariant?.b2bMinimumOrderQuantity || 0,
    };
  };

  // Updated handleAddToCart with tracking
  const handleAddToCart = async () => {
    try {
      if (!getDisplayQuantity() || getDisplayQuantity() <= 0) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.OUT_OF_STOCK_MESSAGE", { 
          productName: product?.productName || 'Product' 
        }));
        return;
      }

      if (product?.hasVariants && !selectedVariant) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.SELECT_VARIANT"));
        return;
      }

      const currentBusinessUuid = getCurrentBusinessUuid();
      if (!currentBusinessUuid) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.STORE_INFO_MISSING"));
        return;
      }

      // Check if user is logged in
      const userProfile = getUserProfile();
      
      if (!userProfile || userProfile.isGuest) {
        localStorage.setItem('pending_cart_item', JSON.stringify({
          businessUuid: currentBusinessUuid,
          item: createCartItem(),
          action: 'add_to_cart'
        }));
        setShowLoginDialog(true);
        toast.info(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LOGIN_REQUIRED"));
        return;
      }
      
      await performAddToCart(userProfile);
      
    } catch (error) {
      console.error("Error in handleAddToCart:", error);
      toast.error(error.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.ADD_TO_CART_FAILED"));
    }
  };

  // Updated handleBuyNow with tracking
  const handleBuyNow = async () => {
    try {
      if (!getDisplayQuantity() || getDisplayQuantity() <= 0) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.OUT_OF_STOCK_MESSAGE", { 
          productName: product?.productName || 'Product' 
        }));
        return;
      }

      if (product?.hasVariants && !selectedVariant) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.SELECT_VARIANT"));
        return;
      }

      const currentBusinessUuid = getCurrentBusinessUuid();
      if (!currentBusinessUuid) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.STORE_INFO_MISSING"));
        return;
      }

      const userProfile = getUserProfile();
      
      if (!userProfile || userProfile.isGuest) {
        localStorage.setItem('pending_buy_now_item', JSON.stringify({
          businessUuid: currentBusinessUuid,
          item: createCartItem(),
          action: 'buy_now'
        }));
        setShowLoginDialog(true);
        toast.info(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.LOGIN_REQUIRED"));
        return;
      }
      
      await performBuyNow(userProfile);
      
    } catch (error) {
      console.error("Error in handleBuyNow:", error);
      toast.error(error.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.BUY_NOW_FAILED"));
    }
  };

  // Add to cart function with tracking
  const performAddToCart = async (userProfile) => {
    setIsProcessing(true);
    try {
      const cartItem = createCartItem();
      const currentBusinessUuid = getCurrentBusinessUuid();
      
      const customerId = userProfile.customerId;
      
      
      if (!customerId) {
        throw new Error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.CUSTOMER_ID_NOT_FOUND"));
      }
      
      const response = await addToUserCart(currentBusinessUuid, customerId, cartItem);
      
      if (response && response.success) {
        // Track add to cart event
        if (typeof trackAddToCart === 'function') {
          trackAddToCart(cartItem._id, currentBusinessUuid);
        }
        
        const cartResponse = await getUserCart(currentBusinessUuid, customerId);
        if (cartResponse && cartResponse.success && cartResponse.data && cartResponse.data.items) {
          setCart(cartResponse.data.items);
        }
        
        toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.ADD_TO_CART_SUCCESS", { 
          productName: cartItem.productName 
        }));
      } else {
        console.error("Add to cart failed:", response);
        toast.error(response?.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.ADD_TO_CART_FAILED"));
      }
    } catch (error) {
      console.error("Error in performAddToCart:", error);
      toast.error(error.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.ADD_TO_CART_FAILED"));
    } finally {
      setIsProcessing(false);
    }
  };

  const performBuyNow = async (userProfile) => {
    setIsProcessing(true);
    try {
      const cartItem = createCartItem();
      const currentBusinessUuid = getCurrentBusinessUuid();
      
      const customerId = userProfile.customerId;
      
      
      if (!customerId) {
        throw new Error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.CUSTOMER_ID_NOT_FOUND"));
      }
      
      const response = await buyNowUser(currentBusinessUuid, customerId, cartItem);
      
      if (response && response.success === true) {
        if (typeof trackAddToCart === 'function') {
          trackAddToCart(cartItem._id, currentBusinessUuid);
        }
        
        toast.success(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.BUY_NOW_SUCCESS"));
        
        const cartResponse = await getUserCart(currentBusinessUuid, customerId);
        if (cartResponse && cartResponse.success && cartResponse.data && cartResponse.data.items) {
          setCart(cartResponse.data.items);
        }
        
        const businessSlug = businessName || business?.businessName || "store";
        const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
        
        // Pass state with cart data
        navigate(`/${formattedSlug}/store/${currentBusinessUuid}/cart`, { 
          state: { 
            cart: [cartItem],
            businessUuid: currentBusinessUuid 
          } 
        });
      } else {
        console.error("Buy now failed:", response);
        toast.error(response?.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.BUY_NOW_FAILED"));
      }
    } catch (error) {
      console.error("Error in performBuyNow:", error);
      toast.error(error.message || t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.BUY_NOW_FAILED"));
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleLoginSuccess = async (userData) => {
    try {
      const fingerprint = await getFingerprint();
      const currentBusinessUuid = getCurrentBusinessUuid();
      if (!currentBusinessUuid) {
        toast.error(t("STOREPRODUCT.PRODUCT_PAGE.MESSAGES.STORE_INFO_MISSING"));
        return;
      }
      
      const payload = {
        customer_mobile: userData.phone,
        name: userData.name,
        returnExisting: true,
        fingerprint,
      };
      
      const response = await createCustomer(currentBusinessUuid, payload);
      
      let customerData;
      
      if (response?.data) {
        customerData = response.data;
        
        const realCustomerId = customerData.customerId;
        
        customerData = {
          customerId: realCustomerId,
          name: customerData.name || userData.name,
          customer_mobile: customerData.customer_mobile || userData.phone,
          phone: userData.phone,
          isGuest: false,
          isCustomer: true,
          ...customerData
        };
        
      } else {
        console.error("No data in response from createCustomer");
        toast.error(t("STOREPRODUCT.LOGIN_DIALOG.ERRORS.CREATE_FAILED"));
        return;
      }
      
      localStorage.setItem(`user_${currentBusinessUuid}`, JSON.stringify(customerData));
      localStorage.removeItem(`guest_${currentBusinessUuid}`);
      
      setShowLoginDialog(false);
      
      const pendingCartItem = localStorage.getItem('pending_cart_item');
      const pendingBuyNowItem = localStorage.getItem('pending_buy_now_item');
      
      
      if (pendingCartItem) {
        const { item } = JSON.parse(pendingCartItem);
        
        await performAddToCart(customerData);
        localStorage.removeItem('pending_cart_item');
      } else if (pendingBuyNowItem) {
        const { item } = JSON.parse(pendingBuyNowItem);
        
        await performBuyNow(customerData);
        localStorage.removeItem('pending_buy_now_item');
      }
      
      toast.success(t("STOREPRODUCT.LOGIN_DIALOG.SUCCESS"));
      
    } catch (error) {
      console.error('Error in handleLoginSuccess:', error);
      toast.error(t("STOREPRODUCT.LOGIN_DIALOG.ERRORS.CREATE_FAILED"));
    }
  };

  const handleGuestMode = () => {
    setShowLoginDialog(false);
    setPendingAction(null);
    localStorage.removeItem('pending_cart_item');
    localStorage.removeItem('pending_buy_now_item');
    toast.info(t("STOREPRODUCT.LOGIN_DIALOG.GUEST_MODE_MESSAGE"));
  };

  const handleReviewAdded = () => {
    setNewReviewAdded((prev) => !prev);
  };

  const renderStars = (rating, size = "text-base") => {
    const numericRating = Number(rating) || 0;
    return (
      <div className={`flex ${size} gap-0.5`} style={{ color: "#FFD700" }}>
        {[1, 2, 3, 4, 5].map((star) =>
          star <= numericRating ? <FaStar key={star} /> : <FaRegStar key={star} />
        )}
      </div>
    );
  };

  const incrementQuantity = () => {
    const maxQty = getDisplayQuantity();
    if (quantity < maxQty) setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) setQuantity(prev => prev - 1);
  };

  const handleBackToStore = () => {
    const currentBusinessUuid = getCurrentBusinessUuid();
    const businessSlug = businessName || business?.businessName || "store";
    const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
    navigate(`/${formattedSlug}/store/${currentBusinessUuid}`);
  };

  const handleGoToCart = () => {
    const currentBusinessUuid = getCurrentBusinessUuid();
    const businessSlug = businessName || business?.businessName || "store";
    const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
    navigate(`/${formattedSlug}/store/${currentBusinessUuid}/cart`);
  };

  const handleGoToWishlist = () => {
    const currentBusinessUuid = getCurrentBusinessUuid();
    const businessSlug = businessName || business?.businessName || "store";
    const formattedSlug = businessSlug.toLowerCase().replace(/\s+/g, '-');
    navigate(`/${formattedSlug}/store/${currentBusinessUuid}/wishlist`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t("STOREPRODUCT.PRODUCT_PAGE.LOADING")}</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-100 rounded-full h-24 w-24 flex items-center justify-center mx-auto mb-6">
            <MdClose className="h-12 w-12 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">{t("STOREPRODUCT.PRODUCT_PAGE.PRODUCT_NOT_FOUND")}</h2>
          <p className="text-gray-600 mb-6">{error || t("STOREPRODUCT.PRODUCT_PAGE.PRODUCT_NOT_FOUND_MESSAGE")}</p>
          <button
            onClick={handleBackToStore}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {t("STOREPRODUCT.PRODUCT_PAGE.GO_TO_STORE")}
          </button>
        </div>
      </div>
    );
  }

  const {
    productName = "Unnamed Product",
    description = "",
    productDetails = "",
    productSize = "",
    unitSize = "",
    showReturn = false,
    returnPeriod = "",
    hasVariants = false,
    variants = [],
    brand = "",
    category = "",
    tags = [],
    warranty = "",
  } = product;

  const discountPercentage = getDiscountPercentage();
  const allImages = getAllImages();
  const inStock = getDisplayQuantity() > 0;
  const displayImage = getDisplayImage();

  return (
    <div 
  className="h-screen flex flex-col bg-gray-50 overflow-hidden"
  style={{ fontFamily: themeStyles.fontFamily }}
>
  {/* Fixed Header */}
  <div className="flex-shrink-0 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm z-10">
    <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
      <div className="flex items-center justify-between">
        <button
          onClick={handleBackToStore}
          className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
          style={{ color: themeStyles.primaryColor }}
        >
          <div className="p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200">
            <FaArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </div>
          <span className="text-xs sm:text-sm font-medium hidden xs:block sm:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.BACK")}</span>
          <span className="text-sm font-medium hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.BACK_TO_STORE")}</span>
        </button>
        
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Wishlist Button */}
          <button
            onClick={handleToggleWishlist}
            disabled={checkingWishlist || togglingWishlist}
            className={`p-1.5 sm:p-2 rounded-full shadow-md hover:shadow-lg transition-all ${
              isWishlisted ? 'bg-red-50' : 'bg-white'
            }`}
            style={{
              color: isWishlisted ? '#ef4444' : themeStyles.primaryColor,
            }}
            title={isWishlisted ? t('PRODUCT_PAGE.REMOVE_FROM_WISHLIST') : t('PRODUCT_PAGE.ADD_TO_WISHLIST')}
          >
            {checkingWishlist || togglingWishlist ? (
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-current border-t-transparent"></div>
            ) : (
              isWishlisted ? <FaHeart className="h-4 w-4 sm:h-5 sm:w-5" /> : <FaRegHeart className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </button>

          {/* Cart Icon with Badge */}
          <button
            onClick={handleGoToCart}
            className="p-1.5 sm:p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all relative"
          >
            <FaShoppingCart className='h-4 w-4 sm:h-5 sm:w-5 text-gray-700' />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center"
                    style={{ backgroundColor: themeStyles.primaryColor }}>
                {cart.reduce((sum, item) => sum + (item.cartQuantity || 1), 0)}
              </span>
            )}
          </button>

          <div className="relative share-menu-container">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="p-1.5 sm:p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all"
            >
              <FaShareAlt className='h-4 w-4 sm:h-5 sm:w-5 text-gray-700' />
            </button>

            {showShareMenu && (
              <div className="absolute top-10 sm:top-12 right-0 bg-white rounded-2xl shadow-2xl p-2 sm:p-3 min-w-[240px] sm:min-w-[280px] z-50 border border-gray-100">
                <div className="text-xs sm:text-sm font-semibold text-gray-800 px-2 sm:px-3 py-1.5 sm:py-2 border-b">
                  {t("STOREPRODUCT.PRODUCT_PAGE.SHARE_PRODUCT")}
                </div>
                
                {shortLink && (
                  <div className="mx-2 sm:mx-3 my-1.5 sm:my-2 p-1.5 sm:p-2 bg-gray-50 rounded-lg border">
                    <div className="text-xs text-gray-500 mb-1">{t("STOREPRODUCT.PRODUCT_PAGE.SHARE_SHORT_LINK")}</div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-xs sm:text-sm font-medium text-gray-800 truncate flex-1">
                        {shortLink}
                      </span>
                      <button
                        onClick={copyToClipboard}
                        className="p-1 sm:p-1.5 rounded-lg hover:bg-white"
                        style={{ color: themeStyles.primaryColor }}
                      >
                        <FaCopy className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-4 gap-1 sm:gap-2 p-1 sm:p-2">
                  <button onClick={shareOnWhatsApp} className="flex flex-col items-center p-1 sm:p-2 rounded-xl hover:bg-gray-50">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center mb-0.5 sm:mb-1">
                      <FaWhatsapp className="text-green-600 text-base sm:text-xl" />
                    </div>
                    <span className="text-[10px] sm:text-xs">WhatsApp</span>
                  </button>
                  <button onClick={shareOnFacebook} className="flex flex-col items-center p-1 sm:p-2 rounded-xl hover:bg-gray-50">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center mb-0.5 sm:mb-1">
                      <FaFacebookF className="text-blue-600 text-base sm:text-xl" />
                    </div>
                    <span className="text-[10px] sm:text-xs">Facebook</span>
                  </button>
                  <button onClick={shareOnTwitter} className="flex flex-col items-center p-1 sm:p-2 rounded-xl hover:bg-gray-50">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-sky-100 flex items-center justify-center mb-0.5 sm:mb-1">
                      <FaTwitter className="text-sky-500 text-base sm:text-xl" />
                    </div>
                    <span className="text-[10px] sm:text-xs">Twitter</span>
                  </button>
                  <button onClick={shareViaEmail} className="flex flex-col items-center p-1 sm:p-2 rounded-xl hover:bg-gray-50">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 flex items-center justify-center mb-0.5 sm:mb-1">
                      <MdEmail className="text-red-500 text-base sm:text-xl" />
                    </div>
                    <span className="text-[10px] sm:text-xs">Email</span>
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setShowQRModal(true);
                    setShowShareMenu(false);
                  }}
                  className="w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-gray-50 rounded-xl"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FaQrcode className="text-purple-600 text-sm sm:text-lg" />
                  </div>
                  <span className="text-xs sm:text-sm">{t("STOREPRODUCT.PRODUCT_PAGE.GENERATE_QR")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Main Content */}
  <div className="flex-1 container mx-auto px-3 sm:px-4 py-4 sm:py-6 overflow-hidden">
    <div className="h-full flex flex-col lg:flex-row gap-4 sm:gap-6">
      {/* Left Side - Image Section */}
      <div className="lg:w-1/2 h-full overflow-hidden">
        <div className="h-full bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-3 sm:p-6 sticky top-0 flex flex-col">
          <div className="relative group flex-1 min-h-[250px] xs:min-h-[300px] sm:min-h-[400px] lg:min-h-0">
            <div
              className='relative w-full h-full rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-gray-200 cursor-zoom-in bg-gray-50'
              onClick={() => openProductImagePopup(selectedImage)}
            >
              {displayImage ? (
                <img
                  className='w-full h-full object-contain p-1 sm:p-4'
                  src={`${AWS_URL}${displayImage}`}
                  alt={getDisplayName()}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/></svg></div>';
                  }}
                />
              ) : (
                <DummyImage className="w-full h-full" />
              )}
              
              <div className='absolute bottom-2 sm:bottom-4 right-2 sm:right-4 p-1 sm:p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg'>
                <MdZoomIn size={14} className='sm:w-5 sm:h-5 text-gray-700' />
              </div>
              
              {discountPercentage > 0 && (
                <div
                  className="absolute top-2 sm:top-4 left-2 sm:left-4 text-white font-bold px-1.5 sm:px-3 py-0.5 sm:py-1.5 text-xs sm:text-sm shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${themeStyles.primaryColor}, ${themeStyles.secondaryColor})`,
                    borderRadius: getButtonBorderRadius(),
                  }}
                >
                  {discountPercentage}% OFF
                </div>
              )}
            </div>

            {allImages.length > 1 && (
              <div className="flex gap-1 sm:gap-2 mt-3 sm:mt-4 overflow-x-auto pb-1 sm:pb-2 h-12 sm:h-20">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedImage(index);
                      if (img) {
                        setSelectedVariant(prev => ({ ...prev, itemPicture: img }));
                      }
                    }}
                    className={`flex-shrink-0 w-10 h-10 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-indigo-600' : 'border-gray-200'
                    }`}
                    style={{ borderColor: selectedImage === index ? themeStyles.primaryColor : undefined }}
                  >
                    {img ? (
                      <img 
                        src={`${AWS_URL}${img}`} 
                        alt={`${t("STOREPRODUCT.PRODUCT_PAGE.VIEW")} ${index + 1}`} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="%23999"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/></svg>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FaImage className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 xs:grid-cols-4 gap-1 sm:gap-3 mt-3 sm:mt-4">
            <div className="text-center p-1.5 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-50 to-white shadow border">
              <FaTruck className="h-3 w-3 sm:h-5 sm:w-5 mx-auto mb-0.5 sm:mb-1" style={{ color: themeStyles.primaryColor }} />
              <div className="text-[10px] sm:text-xs font-semibold hidden xs:block sm:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_SHIPPING_SHORT")}</div>
              <div className="text-[10px] sm:text-xs font-semibold hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_SHIPPING")}</div>
              <div className="text-[8px] sm:text-xs text-gray-500 hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_SHIPPING_DESC")}</div>
            </div>
            <div className="text-center p-1.5 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-50 to-white shadow border">
              <FaShieldAlt className="h-3 w-3 sm:h-5 sm:w-5 mx-auto mb-0.5 sm:mb-1 text-green-600" />
              <div className="text-[10px] sm:text-xs font-semibold hidden xs:block sm:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.SECURE_PAYMENT_SHORT")}</div>
              <div className="text-[10px] sm:text-xs font-semibold hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.SECURE_PAYMENT")}</div>
              <div className="text-[8px] sm:text-xs text-gray-500 hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.SECURE_PAYMENT_DESC")}</div>
            </div>
            <div className="text-center p-1.5 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-50 to-white shadow border">
              <FaExchangeAlt className="h-3 w-3 sm:h-5 sm:w-5 mx-auto mb-0.5 sm:mb-1 text-purple-600" />
              <div className="text-[10px] sm:text-xs font-semibold hidden xs:block sm:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.EASY_RETURNS_SHORT")}</div>
              <div className="text-[10px] sm:text-xs font-semibold hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.EASY_RETURNS")}</div>
              <div className="text-[8px] sm:text-xs text-gray-500 hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.EASY_RETURNS_DESC", { days: returnPeriod || '7' })}</div>
            </div>
            <div className="text-center p-1.5 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-50 to-white shadow border">
              <FaClock className="h-3 w-3 sm:h-5 sm:w-5 mx-auto mb-0.5 sm:mb-1 text-orange-600" />
              <div className="text-[10px] sm:text-xs font-semibold hidden xs:block sm:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_DELIVERY_SHORT")}</div>
              <div className="text-[10px] sm:text-xs font-semibold hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_DELIVERY")}</div>
              <div className="text-[8px] sm:text-xs text-gray-500 hidden sm:block">{t("STOREPRODUCT.PRODUCT_PAGE.FEATURES.FAST_DELIVERY_DESC")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Product Details */}
      <div className="lg:w-1/2 h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 pb-2 sm:pb-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-6">
            {(brand || category) && (
              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2 sm:mb-4">
                {brand && <span className="text-[10px] sm:text-sm bg-gray-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">{t("STOREPRODUCT.PRODUCT_PAGE.BRAND")}: {brand}</span>}
                {category && <span className="text-[10px] sm:text-sm bg-gray-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">{t("STOREPRODUCT.PRODUCT_PAGE.CATEGORY")}: {category}</span>}
              </div>
            )}

            <h1 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 leading-tight" style={{ color: themeStyles.headingColor }}>
              {getDisplayName()}
            </h1>

            {reviewStats && reviewStats.totalReviews > 0 && (
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="flex items-center">
                  {renderStars(reviewStats.averageRating, "text-base sm:text-lg")}
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-semibold">
                    {reviewStats.averageRating.toFixed(1)}
                  </span>
                </div>
                <span className="text-xs sm:text-sm text-gray-500">
                  ({reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? t("STOREPRODUCT.REVIEW_SECTION.REVIEW") : t("STOREPRODUCT.REVIEW_SECTION.REVIEWS")})
                </span>
              </div>
            )}

            <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-r from-gray-50 to-white border">
              <div className="flex flex-wrap items-baseline gap-2 sm:gap-3">
                <span className="text-2xl sm:text-3xl font-bold" style={{ color: themeStyles.primaryColor }}>
                  ₹{getDisplayPrice()}
                </span>
                {getOriginalPrice() > getDisplayPrice() && (
                  <>
                    <span className="text-base sm:text-lg text-gray-400 line-through">
                      ₹{getOriginalPrice()}
                    </span>
                    <span className="text-[10px] sm:text-sm font-bold text-green-600 bg-green-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      {t("STOREPRODUCT.PRODUCT_PAGE.SAVE")} {discountPercentage}%
                    </span>
                  </>
                )}
              </div>
            </div>

            {hasVariants && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gray-50 border">
                <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">{t("STOREPRODUCT.PRODUCT_PAGE.SELECT_OPTIONS")}</h3>
                <VariantSelector
                  product={product}
                  selectedVariant={selectedVariant}
                  onVariantSelect={(variant) => {
                    setSelectedVariant(variant);
                    setQuantity(1);
                  }}
                  theme={theme}
                />
              </div>
            )}

            {description && (
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{t("STOREPRODUCT.PRODUCT_PAGE.DESCRIPTION")}</h3>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {description}
                </p>
              </div>
            )}

            {productDetails && (
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{t("STOREPRODUCT.PRODUCT_PAGE.PRODUCT_DETAILS")}</h3>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {productDetails}
                </p>
              </div>
            )}

            {tags && tags.length > 0 && (
              <div className="mb-4 sm:mb-6 flex flex-wrap gap-1 sm:gap-2">
                {tags.map((tag, index) => (
                  <span key={index} className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 text-gray-600 text-[10px] sm:text-xs rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            <div className="mb-4 sm:mb-6 grid grid-cols-2 gap-2 sm:gap-3">
              {productSize && (
                <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <span className="text-[10px] sm:text-xs text-gray-500">{t("STOREPRODUCT.PRODUCT_PAGE.SIZE")}</span>
                  <p className="font-medium text-xs sm:text-sm"> {unitSize}</p>
                </div>
              )}
              {warranty && (
                <div className="p-2 sm:p-3 bg-gray-50 rounded-lg">
                  <span className="text-[10px] sm:text-xs text-gray-500">{t("STOREPRODUCT.PRODUCT_PAGE.WARRANTY")}</span>
                  <p className="font-medium text-xs sm:text-sm">{warranty}</p>
                </div>
              )}
            </div>

            <div className="mb-4 sm:mb-6">
              <ReviewSection
                product={product}
                bussinessUuid={resolvedBusinessUuid}
                customerProfile={customerProfile}
                theme={theme}
                onReviewAdded={handleReviewAdded}
                reviewStats={reviewStats}
              />
            </div>
          </div>
        </div>

        {/* Sticky Action Buttons */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 shadow-lg p-3 sm:p-4 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex gap-2 sm:gap-4">
            {isVariantInCart ? (
              <button
                onClick={handleGoToCart}
                className="flex-1 py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base font-semibold text-white hover:opacity-90 transition-all"
                style={{
                  background: '#10b981',
                  borderRadius: getButtonBorderRadius(),
                }}
              >
                <span className="flex items-center justify-center gap-1 sm:gap-2">
                  <FaShoppingCart className='h-3 w-3 sm:h-4 sm:w-4' />
                  <span className="xs:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.GO_TO_CART")}</span>
                </span>
              </button>
            ) : (
              <>
                <button
                  onClick={handleAddToCart}
                  disabled={!inStock || isProcessing}
                  className="flex-1 py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base font-semibold text-white hover:opacity-90 transition-all disabled:opacity-50"
                  style={{
                    background: !inStock ? '#9ca3af' : themeStyles.buttonColor,
                    borderRadius: getButtonBorderRadius(),
                  }}
                >
                  <span className="flex items-center justify-center gap-1 sm:gap-2">
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <FaShoppingCart className='h-3 w-3 sm:h-4 sm:w-4' />
                    )}
                    {isProcessing ? t("STOREPRODUCT.PRODUCT_PAGE.ADDING") : (inStock ? 
                      <><span className="hidden xs:inline">{t("STOREPRODUCT.PRODUCT_PAGE.ADD_TO_CART")}</span><span className="xs:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.ADD")}</span></> : 
                      <span className="text-xs sm:text-sm">{t("STOREPRODUCT.PRODUCT_PAGE.OUT_OF_STOCK")}</span>
                    )}
                  </span>
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={!inStock || isProcessing}
                  className="flex-1 py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base font-semibold text-white hover:opacity-90 transition-all disabled:opacity-50"
                  style={{
                    background: !inStock ? '#9ca3af' : '#10b981',
                    borderRadius: getButtonBorderRadius(),
                  }}
                >
                  <span className="flex items-center justify-center gap-1 sm:gap-2">
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <MdLocalShipping className='h-3 w-3 sm:h-4 sm:w-4' />
                    )}
                    {isProcessing ? t("STOREPRODUCT.PRODUCT_PAGE.PROCESSING") : 
                      <><span className="hidden xs:inline">{t("STOREPRODUCT.PRODUCT_PAGE.BUY_NOW")}</span><span className="xs:hidden">{t("STOREPRODUCT.PRODUCT_PAGE.BUY")}</span></>
                    }
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Image Popup */}
  {imagePopup.isOpen && (
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-95'
      onClick={closeImagePopup}
    >
      <button onClick={closeImagePopup} className='absolute top-2 sm:top-4 right-2 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20'>
        <MdClose className='h-4 w-4 sm:h-6 sm:w-6 text-white' />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setImagePopup(prev => ({
            ...prev,
            currentIndex: prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.images.length - 1
          }));
        }}
        className='absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20'
      >
        <FaArrowLeft className='h-4 w-4 sm:h-6 sm:w-6 text-white' />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setImagePopup(prev => ({
            ...prev,
            currentIndex: prev.currentIndex < prev.images.length - 1 ? prev.currentIndex + 1 : 0
          }));
        }}
        className='absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20'
      >
        <FaArrowLeft className='h-4 w-4 sm:h-6 sm:w-6 text-white rotate-180' />
      </button>

      <div className='relative max-w-7xl max-h-[90vh] px-2 sm:px-4' onClick={(e) => e.stopPropagation()}>
        {imagePopup.images[imagePopup.currentIndex]?.isDummy ? (
          <DummyImage className="w-48 h-48 sm:w-96 sm:h-96 rounded-lg" icon={FaImage} />
        ) : (
          <img
            src={imagePopup.images[imagePopup.currentIndex]?.url}
            alt={imagePopup.images[imagePopup.currentIndex]?.alt}
            className='max-w-full max-h-[70vh] sm:max-h-[80vh] object-contain'
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<div class="w-48 h-48 sm:w-96 sm:h-96 bg-gray-800 rounded-lg flex items-center justify-center"><svg class="w-16 h-16 sm:w-24 sm:h-24 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/></svg></div>';
            }}
          />
        )}
      </div>
    </div>
  )}

  {/* QR Modal */}
  {showQRModal && (
    <QRCodeModal
      url={shortLink || getProductShareUrl(businessName, bussinessUuid, productId)}
      onClose={() => setShowQRModal(false)}
      theme={theme}
      productName={product?.productName}
      setShowShareMenu={setShowShareMenu}
    />
  )}

  {/* Login Dialog */}
  {showLoginDialog && (
    <AccountLoginDialog
      open={showLoginDialog}
      onClose={() => {
        setShowLoginDialog(false);
        setPendingAction(null);
      }}
      onLogin={handleLoginSuccess}
      onGuestMode={handleGuestMode}
      theme={theme}
      businessUuid={getCurrentBusinessUuid()}
      showGuestButton={false} 
    />
  )}
</div>
  );
}

export default ProductPage;